import { useState } from 'react';

import { api, type NumberPermission, type PhoneNumber, type User } from './api.js';

interface Props {
  user: User | null;
  numbers: PhoneNumber[];
  onDone: (temporaryPassword?: string) => void;
  onCancel: () => void;
}

/**
 * User form with the per-number permission matrix.
 *
 * Inbound and outbound are separate on purpose. A shared sales line might ring
 * three people while only one is allowed to dial out presenting it, and the
 * previous model — one flat list of "assigned" numbers — could not express
 * that.
 */
export function UserForm({ user, numbers, onDone, onCancel }: Props): JSX.Element {
  const [form, setForm] = useState({
    email: user?.email ?? '',
    displayName: user?.displayName ?? '',
    extension: user?.extension ?? '',
    role: user?.role ?? ('agent' as User['role']),
  });

  const [perms, setPerms] = useState<Record<string, NumberPermission>>(() => {
    const initial: Record<string, NumberPermission> = {};
    for (const n of numbers) {
      const existing = user?.numbers.find((x) => x.id === n.id);
      initial[n.id] = {
        numberId: n.id,
        present: existing?.canPresent ?? false,
        receive: existing?.canReceive ?? false,
        isDefault: existing?.isDefault ?? false,
      };
    }
    return initial;
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(numberId: string, field: 'present' | 'receive'): void {
    setPerms((p) => {
      const row = p[numberId]!;
      const next = { ...row, [field]: !row[field] };
      // A number that can no longer be presented cannot be the default.
      if (field === 'present' && !next.present) next.isDefault = false;
      return { ...p, [numberId]: next };
    });
  }

  function setDefault(numberId: string): void {
    setPerms((p) => {
      const out: Record<string, NumberPermission> = {};
      for (const [id, row] of Object.entries(p)) {
        out[id] = { ...row, isDefault: id === numberId };
      }
      return out;
    });
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const selected = Object.values(perms).filter((p) => p.present || p.receive);

    // Without a default the dialplan just picks the first presentable number,
    // which is fine but arbitrary — better to choose one here.
    const presentable = selected.filter((p) => p.present);
    if (presentable.length > 0 && !presentable.some((p) => p.isDefault)) {
      presentable[0]!.isDefault = true;
    }

    try {
      if (user) {
        await api.updateUser(user.id, { ...form, numbers: selected });
        onDone();
      } else {
        const result = await api.createUser({ ...form, numbers: selected });
        onDone(result.temporaryPassword);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <form className="card form" onSubmit={(e) => void submit(e)}>
      <h2>{user ? `Edit ${user.displayName}` : 'Add user'}</h2>

      <div className="grid-3">
        <label>
          <span>Full name</span>
          <input
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            required
          />
        </label>
        <label>
          <span>Email</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </label>
        <label>
          <span>Extension</span>
          <input
            value={form.extension}
            onChange={(e) => setForm({ ...form, extension: e.target.value })}
            placeholder="1001"
            required
          />
        </label>
      </div>

      <label>
        <span>Role</span>
        <select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value as User['role'] })}
        >
          <option value="agent">Agent — own calls only</option>
          <option value="supervisor">Supervisor — sees all calls and transcripts</option>
          <option value="admin">Admin — full access including trunks</option>
        </select>
      </label>

      <h3>Number permissions</h3>
      <p className="hint">
        <strong>Inbound</strong> means calls to that number ring this user — several
        people can share one line. <strong>Outbound</strong> means they may present it
        as caller ID. They are independent.
      </p>

      {numbers.length === 0 ? (
        <p className="hint">No numbers configured yet. Add one under Numbers first.</p>
      ) : (
        <table className="perm-table">
          <thead>
            <tr>
              <th>Number</th>
              <th className="perm-col">Inbound</th>
              <th className="perm-col">Outbound</th>
              <th className="perm-col">Default</th>
            </tr>
          </thead>
          <tbody>
            {numbers.map((n) => {
              const row = perms[n.id]!;
              return (
                <tr key={n.id}>
                  <td>
                    {n.e164}
                    {n.label && <span className="perm-label"> {n.label}</span>}
                  </td>
                  <td className="perm-col">
                    <input
                      type="checkbox"
                      checked={row.receive}
                      onChange={() => toggle(n.id, 'receive')}
                      aria-label={`Inbound on ${n.e164}`}
                    />
                  </td>
                  <td className="perm-col">
                    <input
                      type="checkbox"
                      checked={row.present}
                      onChange={() => toggle(n.id, 'present')}
                      aria-label={`Outbound from ${n.e164}`}
                    />
                  </td>
                  <td className="perm-col">
                    <input
                      type="radio"
                      name="defaultNumber"
                      checked={row.isDefault}
                      disabled={!row.present}
                      onChange={() => setDefault(n.id)}
                      aria-label={`Default caller ID ${n.e164}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button type="button" className="secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" disabled={busy}>
          {busy ? 'Saving…' : user ? 'Save changes' : 'Add user'}
        </button>
      </div>
    </form>
  );
}
